// ==========================================================
// File: AdminAnnouncementEditor.jsx
// Purpose: Weekly Challenge Announcement (Admin)
// ==========================================================

import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";

import CampaignIcon from "@mui/icons-material/Campaign";
import PreviewIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";

import api from "../../utils/api";

export default function AdminAnnouncementEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [weekStart, setWeekStart] = useState(null);

  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  // ==========================================================
  // Load current weekly announcement
  // ==========================================================
  useEffect(() => {
    api
      .get("/challenges/weekly-announcement")
      .then((res) => {
        setMessage(res.data?.message || "");
        setSubjects(res.data?.subjects || []);
        setWeekStart(res.data?.week_start || null);
      })
      .catch(() => {
        setError("Failed to load weekly announcement.");
      })
      .finally(() => setLoading(false));
  }, []);

  // ==========================================================
  // Save announcement
  // ==========================================================
  const handleSave = async () => {
    if (!message.trim()) {
      setError("Announcement message cannot be empty.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await api.post("/challenges/weekly-announcement", {
        message,
      });
      setSuccess("Weekly announcement saved successfully.");
    } catch {
      setError("Failed to save announcement.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      {/* ======================================================
          Header
         ====================================================== */}
      <Stack direction="row" spacing={2} alignItems="center" mb={3}>
        <CampaignIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">
          Weekly Challenge Announcement
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        This message will be displayed to all students for the current
        week’s challenges. It resets automatically every Monday.
      </Typography>

      {/* ======================================================
          Alerts
         ====================================================== */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* ======================================================
          Editor
         ====================================================== */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Typography fontWeight="bold" mb={1}>
          Announcement Message
        </Typography>

        <TextField
          fullWidth
          multiline
          minRows={4}
          placeholder="📣 Example: This week’s challenge focuses on Mathematics and English..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={2}
          mt={3}
        >
          <Typography variant="caption" color="text.secondary">
            Week start: {weekStart || "Not set yet"}
          </Typography>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Announcement"}
          </Button>
        </Stack>
      </Paper>

      {/* ======================================================
          Preview
         ====================================================== */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 3,
          borderLeft: "6px solid #1976d2",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <PreviewIcon color="primary" />
          <Typography fontWeight="bold">
            Student Preview
          </Typography>
        </Stack>

        {message ? (
          <Alert severity="info">
            <Stack spacing={1}>
              <Typography>{message}</Typography>

              {subjects.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {subjects.map((s) => (
                    <Chip
                      key={s.id}
                      label={`${s.name} (${s.code})`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Alert>
        ) : (
          <Typography color="text.secondary">
            No announcement set for this week.
          </Typography>
        )}
      </Paper>

      <Divider sx={{ my: 4 }} />

      <Typography variant="caption" color="text.secondary">
        Note: Subjects displayed here are controlled in the Leaderboard
        Manager and update automatically.
      </Typography>
    </Box>
  );
}
